// Click-to-call helper: lazily brings the Twilio Voice browser device online and
// places a client -> PSTN outbound call. Reuses the existing voice-device singleton
// and communications API so lead/customer cards can dial in-app without depending on
// the Communications page being mounted first.
import api from "../apiClient";
import {
  fetchVoiceClientToken,
  registerVoiceDevice,
  startOutboundCall,
} from "../api/communications";
import { initVoiceDevice, getVoiceDevice } from "./voiceDevice";

// De-dupe concurrent bring-up attempts (e.g. rapid double clicks).
let readyPromise = null;

/**
 * Ensure the Twilio Voice device is registered and ready.
 * Idempotent: returns the existing singleton if already initialized.
 * Throws a coded Error ("microphone_permission_denied", "voice_unavailable",
 * "missing_tenant") so callers can fall back to a tel: link gracefully.
 */
export async function ensureVoiceReady(tenantId) {
  const existing = getVoiceDevice();
  if (existing) return existing;
  if (readyPromise) return readyPromise;

  readyPromise = (async () => {
    if (!tenantId) {
      const err = new Error("missing_tenant");
      err.code = "missing_tenant";
      throw err;
    }

    const clientToken = await fetchVoiceClientToken(tenantId);
    const registration = await registerVoiceDevice(clientToken, tenantId);

    // Mic permission is required before the SDK can open an outbound connection.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (micErr) {
      const err = new Error("microphone_permission_denied");
      err.code = "microphone_permission_denied";
      err.cause = micErr;
      throw err;
    }

    return initVoiceDevice(
      registration?.webrtcToken ||
        registration?.client_token ||
        registration?.access_token ||
        clientToken,
      { iceServers: registration?.iceServers }
    );
  })();

  try {
    return await readyPromise;
  } finally {
    readyPromise = null;
  }
}

/**
 * Resolve the tenant's default outbound caller-ID number.
 * Prefers the primary number, then the first active one. Returns "" on failure.
 */
export async function fetchDefaultFromNumber() {
  try {
    const res = await api.get("/comms/phone-numbers/");
    const rows = Array.isArray(res.data?.phone_numbers)
      ? res.data.phone_numbers
      : res.data || [];
    const mapped = rows
      .map((n) => ({
        number: n.phone_number || n.number || n.value || "",
        is_primary: Boolean(n.is_primary || n.primary || n.is_default),
      }))
      .filter((n) => n.number);
    if (!mapped.length) return "";
    const primary = mapped.find((n) => n.is_primary);
    return (primary || mapped[0]).number;
  } catch {
    return "";
  }
}

/**
 * Place a client -> PSTN outbound call through the already-ready device.
 * Passes To / tenant_id / user_id so the voice service /twilio/outbound TwiML
 * route can resolve the tenant caller-ID and bridge the call.
 * Returns the Twilio Call (connection) object so the caller can attach listeners
 * and hang up.
 */
export async function placeOutboundCall({
  device,
  tenantId,
  userId,
  fromNumber,
  toNumber,
}) {
  if (!device) throw new Error("voice_unavailable");
  if (!toNumber) throw new Error("missing_to_number");

  // Prime the voice-service session + refresh the access token (mirrors WebPhone).
  let priming = {};
  try {
    priming = (await startOutboundCall(fromNumber || "", toNumber)) || {};
  } catch {
    // Non-fatal: the device can still connect; the TwiML route resolves config.
    priming = {};
  }

  const params = {
    To: toNumber,
    to: toNumber,
    tenant_id: String(tenantId || ""),
  };
  if (userId !== undefined && userId !== null && String(userId) !== "") {
    params.user_id = String(userId);
    params.identity = `user:${userId}`;
  }
  if (fromNumber) params.from = fromNumber;
  const primedSid = priming.call_id || priming.callSid || priming.call_sid;
  if (primedSid) params.call_id = String(primedSid);

  // @twilio/voice-sdk v2: device.connect() resolves to a Call object.
  return device.connect({ params });
}

/**
 * Best-effort read-back of the server-derived outcome for a placed call.
 * The authoritative outcome is written by the voice service status callbacks
 * (Twilio -> /twilio/status -> CRM call_ended -> derive_call_outcome), so we
 * simply read it back rather than writing a competing value from the client.
 */
export async function readCallOutcome(callSid) {
  if (!callSid) return null;
  try {
    const res = await api.get(`/comms/calls/${callSid}/`);
    return res.data?.outcome || res.data?.status || null;
  } catch {
    return null;
  }
}
